import React, { useState, useEffect } from 'react';
import { X, FileDiff, ArrowRight } from 'lucide-react';
import { protocolService } from '../../services/protocolService';
import Modal from '../ui/Modal';
import './VersionDiffModal.css';

const VersionDiffModal = ({ isOpen, onClose, version1, version2, protocolId }) => {
  const [protocol1, setProtocol1] = useState(null);
  const [protocol2, setProtocol2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [differences, setDifferences] = useState(null);

  useEffect(() => {
    if (isOpen && version1 && version2) {
      loadProtocols();
    }
  }, [isOpen, version1, version2, protocolId]);

  const loadProtocols = async () => {
    try {
      setLoading(true);
      
      // Get protocol IDs from version history
      const protocol1Id = version1.new_protocol_id || version1.original_protocol_id || protocolId;
      const protocol2Id = version2.new_protocol_id || version2.original_protocol_id || protocolId;
      
      const [proto1, proto2] = await Promise.all([
        protocolService.getProtocol(protocol1Id),
        protocolService.getProtocol(protocol2Id)
      ]);
      
      setProtocol1(proto1);
      setProtocol2(proto2);
      
      // Calculate differences
      if (proto1 && proto2) {
        const diff = calculateDifferences(proto1, proto2);
        setDifferences(diff);
      }
    } catch (error) {
      console.error('Error loading protocols for comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDifferences = (v1, v2) => {
    const diff = {
      basic: {},
      steps: {
        added: [],
        removed: [],
        modified: []
      },
      evidence: {
        guidelines: { added: [], removed: [] },
        standards: { added: [], removed: [] },
        regulations: { added: [], removed: [] }
      }
    };

    // Basic Information differences
    const basicFields = ['name', 'category', 'protocol_code', 'description', 'version', 'status'];
    basicFields.forEach(field => {
      if (v1[field] !== v2[field]) {
        diff.basic[field] = {
          old: v1[field] || '',
          new: v2[field] || ''
        };
      }
    });

    // Steps differences
    const steps1 = v1.steps || [];
    const steps2 = v2.steps || [];
    
    // Find added/removed steps
    const stepIds1 = new Set(steps1.map(s => s.id || s.step_id));
    const stepIds2 = new Set(steps2.map(s => s.id || s.step_id));
    
    steps2.forEach(step2 => {
      const stepId = step2.id || step2.step_id;
      if (!stepIds1.has(stepId)) {
        diff.steps.added.push(step2);
      }
    });
    
    steps1.forEach(step1 => {
      const stepId = step1.id || step1.step_id;
      if (!stepIds2.has(stepId)) {
        diff.steps.removed.push(step1);
      }
    });
    
    // Find modified steps
    steps1.forEach(step1 => {
      const stepId = step1.id || step1.step_id;
      const step2 = steps2.find(s => (s.id || s.step_id) === stepId);
      
      if (step2 && JSON.stringify(step1) !== JSON.stringify(step2)) {
        diff.steps.modified.push({
          id: stepId,
          name: step1.name || step1.step_name,
          old: step1,
          new: step2
        });
      }
    });

    // Evidence Base differences
    const evidence1 = v1.evidence_base || {};
    const evidence2 = v2.evidence_base || {};
    
    // Guidelines
    const guidelines1 = evidence1.guidelines || [];
    const guidelines2 = evidence2.guidelines || [];
    diff.evidence.guidelines.added = guidelines2.filter(g2 => 
      !guidelines1.some(g1 => g1.name === g2.name)
    );
    diff.evidence.guidelines.removed = guidelines1.filter(g1 => 
      !guidelines2.some(g2 => g2.name === g1.name)
    );
    
    // Standards
    const standards1 = evidence1.standards || [];
    const standards2 = evidence2.standards || [];
    diff.evidence.standards.added = standards2.filter(s2 => !standards1.includes(s2));
    diff.evidence.standards.removed = standards1.filter(s1 => !standards2.includes(s1));
    
    // Regulations
    const regulations1 = evidence1.regulations || [];
    const regulations2 = evidence2.regulations || [];
    diff.evidence.regulations.added = regulations2.filter(r2 => !regulations1.includes(r2));
    diff.evidence.regulations.removed = regulations1.filter(r1 => !regulations2.includes(r1));

    return diff;
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Version Comparison: ${version1?.new_version || 'v1'} vs ${version2?.new_version || 'v2'}`}
      size="large"
    >
      {loading ? (
        <div className="loading-container">
          <p>Loading versions for comparison...</p>
        </div>
      ) : differences ? (
        <div className="version-diff-container">
          {/* Basic Information Changes */}
          {Object.keys(differences.basic).length > 0 && (
            <div className="diff-section">
              <h3 className="diff-section-title">Basic Information</h3>
              <div className="diff-list">
                {Object.entries(differences.basic).map(([field, change]) => (
                  <div key={field} className="diff-item">
                    <div className="diff-field">
                      <strong>{field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                    </div>
                    <div className="diff-values">
                      <div className="diff-value old">
                        <span className="diff-label">Version {version1?.new_version || 'v1'}:</span>
                        <span className="diff-text">{change.old || '(empty)'}</span>
                      </div>
                      <ArrowRight size={16} className="diff-arrow" />
                      <div className="diff-value new">
                        <span className="diff-label">Version {version2?.new_version || 'v2'}:</span>
                        <span className="diff-text">{change.new || '(empty)'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Steps Changes */}
          {(differences.steps.added.length > 0 || 
            differences.steps.removed.length > 0 || 
            differences.steps.modified.length > 0) && (
            <div className="diff-section">
              <h3 className="diff-section-title">Protocol Steps</h3>
              
              {differences.steps.added.length > 0 && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle added">Added Steps ({differences.steps.added.length})</h4>
                  <div className="diff-list">
                    {differences.steps.added.map((step, idx) => (
                      <div key={idx} className="diff-item added">
                        <span className="diff-icon">+</span>
                        <span>{step.name || step.step_name || 'Unnamed Step'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {differences.steps.removed.length > 0 && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle removed">Removed Steps ({differences.steps.removed.length})</h4>
                  <div className="diff-list">
                    {differences.steps.removed.map((step, idx) => (
                      <div key={idx} className="diff-item removed">
                        <span className="diff-icon">-</span>
                        <span>{step.name || step.step_name || 'Unnamed Step'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {differences.steps.modified.length > 0 && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle modified">Modified Steps ({differences.steps.modified.length})</h4>
                  <div className="diff-list">
                    {differences.steps.modified.map((step, idx) => (
                      <div key={idx} className="diff-item modified">
                        <span className="diff-icon">~</span>
                        <span>{step.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Evidence Base Changes */}
          {(differences.evidence.guidelines.added.length > 0 ||
            differences.evidence.guidelines.removed.length > 0 ||
            differences.evidence.standards.added.length > 0 ||
            differences.evidence.standards.removed.length > 0 ||
            differences.evidence.regulations.added.length > 0 ||
            differences.evidence.regulations.removed.length > 0) && (
            <div className="diff-section">
              <h3 className="diff-section-title">Evidence Base</h3>
              
              {differences.evidence.guidelines.added.length > 0 && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle added">Added Guidelines ({differences.evidence.guidelines.added.length})</h4>
                  <div className="diff-list">
                    {differences.evidence.guidelines.added.map((guideline, idx) => (
                      <div key={idx} className="diff-item added">
                        <span className="diff-icon">+</span>
                        <span>{guideline.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {differences.evidence.guidelines.removed.length > 0 && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle removed">Removed Guidelines ({differences.evidence.guidelines.removed.length})</h4>
                  <div className="diff-list">
                    {differences.evidence.guidelines.removed.map((guideline, idx) => (
                      <div key={idx} className="diff-item removed">
                        <span className="diff-icon">-</span>
                        <span>{guideline.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {(differences.evidence.standards.added.length > 0 || differences.evidence.standards.removed.length > 0) && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle">Standards</h4>
                  {differences.evidence.standards.added.length > 0 && (
                    <div className="diff-list">
                      {differences.evidence.standards.added.map((standard, idx) => (
                        <div key={idx} className="diff-item added">
                          <span className="diff-icon">+</span>
                          <span>{standard}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {differences.evidence.standards.removed.length > 0 && (
                    <div className="diff-list">
                      {differences.evidence.standards.removed.map((standard, idx) => (
                        <div key={idx} className="diff-item removed">
                          <span className="diff-icon">-</span>
                          <span>{standard}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {(differences.evidence.regulations.added.length > 0 || differences.evidence.regulations.removed.length > 0) && (
                <div className="diff-subsection">
                  <h4 className="diff-subtitle">Regulations</h4>
                  {differences.evidence.regulations.added.length > 0 && (
                    <div className="diff-list">
                      {differences.evidence.regulations.added.map((regulation, idx) => (
                        <div key={idx} className="diff-item added">
                          <span className="diff-icon">+</span>
                          <span>{regulation}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {differences.evidence.regulations.removed.length > 0 && (
                    <div className="diff-list">
                      {differences.evidence.regulations.removed.map((regulation, idx) => (
                        <div key={idx} className="diff-item removed">
                          <span className="diff-icon">-</span>
                          <span>{regulation}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* No Changes */}
          {Object.keys(differences.basic).length === 0 &&
            differences.steps.added.length === 0 &&
            differences.steps.removed.length === 0 &&
            differences.steps.modified.length === 0 &&
            differences.evidence.guidelines.added.length === 0 &&
            differences.evidence.guidelines.removed.length === 0 &&
            differences.evidence.standards.added.length === 0 &&
            differences.evidence.standards.removed.length === 0 &&
            differences.evidence.regulations.added.length === 0 &&
            differences.evidence.regulations.removed.length === 0 && (
            <div className="no-changes">
              <p>No differences found between these versions.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="error-container">
          <p>Error loading versions for comparison.</p>
        </div>
      )}
      
      <div className="modal-actions">
        <button onClick={onClose} className="btn btn-primary">
          Close
        </button>
      </div>
    </Modal>
  );
};

export default VersionDiffModal;



